/**
 * AuthorityStack — ascending stack of E-E-A-T signals culminating in citations.
 * Bespoke widening SVG bars (proof compounds upward). i18n; role="img" + <title>.
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface AuthorityStackProps {
  namespace?: string;
  className?: string;
}

const LEVELS = ['l1', 'l2', 'l3', 'l4'] as const;

export function AuthorityStack({ namespace = 'marketing.diagrams.authorityStack', className }: AuthorityStackProps) {
  const t = useTranslations(namespace);
  const titleId = 'authority-stack-title';
  return (
    <MotionReveal direction="up" className="min-w-0">
      <div className={cn('glass-card min-w-0 p-6 lg:p-8', className)}>
        <h3 className="text-display text-lg font-semibold text-white lg:text-xl">{t('title')}</h3>
        <div className="mt-5 grid items-center gap-6 lg:grid-cols-2">
          <svg
            viewBox="0 0 240 200"
            role="img"
            aria-labelledby={titleId}
            className="mx-auto w-full max-w-[260px]"
          >
            <title id={titleId}>{t('title')}</title>
            {[...LEVELS].map((level, idx) => {
              // Bottom (l1) narrow → top (l4) widest = signals compounding into citations.
              const fromBottom = idx; // l1 at bottom
              const y = 156 - fromBottom * 44;
              const width = 90 + idx * 40;
              const x = (240 - width) / 2;
              return (
                <g key={level}>
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height="36"
                    rx="8"
                    fill="url(#authority-grad)"
                    opacity={0.45 + idx * 0.18}
                    stroke="#1aabff"
                    strokeOpacity="0.4"
                  />
                  <text
                    x="120"
                    y={y + 23}
                    textAnchor="middle"
                    fill="#ffffff"
                    fontSize="13"
                    fontWeight="600"
                    fontFamily="var(--font-inter)"
                  >
                    {t(`levels.${level}.title`)}
                  </text>
                </g>
              );
            })}
            <defs>
              <linearGradient id="authority-grad" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="#0d8edb" />
                <stop offset="100%" stopColor="#1aabff" />
              </linearGradient>
            </defs>
          </svg>
          <dl className="space-y-4">
            {[...LEVELS].reverse().map((level) => (
              <div key={level} className="border-l-2 border-cyan-500/30 pl-4">
                <dt className="text-display text-base font-semibold text-cyan-300">
                  {t(`levels.${level}.title`)}
                </dt>
                <dd className="mt-1 text-sm leading-relaxed text-white/70">
                  {t(`levels.${level}.body`)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </MotionReveal>
  );
}
